import { useMemo, useState, type FC } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useTranslation from '@/hooks/useTranslation'
import type { GridFilterProps, GridSetFilterModel } from '../types'
import { isSetFilterActive, toSetFilterModel } from './setFilterModel'

/**
 * Multi-select value list — the replacement for `agSetColumnFilter`.
 *
 * Selections are staged locally and only reported on Apply. The column may be
 * server-filtered (Controllers' Purpose), where every tick would otherwise
 * purge the block cache and refetch, and even client-side the list redrawing
 * under the checkbox you are aiming at is worse than one deliberate button.
 */
const SetFilterPopover: FC<GridFilterProps<unknown>> = ({
  field,
  model,
  rows,
  params,
  onChange,
  onClose,
}) => {
  const { t } = useTranslation()

  // An explicit `values` list is what a server-side column filters on (ids the
  // datasource sends). Without one, offer the distinct values on screen.
  const options = useMemo<unknown[]>(() => {
    if (params && Array.isArray(params.values)) return params.values

    const seen = new Set<string>()
    const distinct: unknown[] = []
    rows.forEach((row) => {
      const value = (row as Record<string, unknown>)?.[field]
      const key = String(value ?? '')
      if (key === '' || seen.has(key)) return
      seen.add(key)
      distinct.push(value)
    })
    return distinct
  }, [field, params, rows])

  const formatValue =
    (params?.formatValue as ((value: unknown) => string) | undefined) ??
    ((value: unknown) => String(value ?? ''))

  const [selected, setSelected] = useState<string[]>(() =>
    isSetFilterActive(model)
      ? (model as GridSetFilterModel).values.map((value) => String(value))
      : [],
  )

  const toggle = (value: unknown) => {
    const key = String(value)
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((entry) => entry !== key) : [...prev, key],
    )
  }

  const apply = () => {
    // Report the original values, not the string keys the checkboxes compare on:
    // the datasource sends these straight to the API, which wants numeric ids.
    const chosen = options.filter((option) => selected.includes(String(option)))
    onChange(toSetFilterModel(chosen))
    onClose()
  }

  const reset = () => {
    setSelected([])
    onChange(null)
    onClose()
  }

  return (
    <Box sx={{ padding: '8px', minWidth: 220 }}>
      {params?.title ? (
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
            color: 'grey.600',
            px: '8px',
            pb: '4px',
          }}
        >
          {String(params.title)}
        </Typography>
      ) : null}

      <Stack sx={{ maxHeight: 260, overflowY: 'auto' }}>
        {options.map((option) => (
          <FormControlLabel
            key={String(option)}
            control={
              <Checkbox
                size="small"
                checked={selected.includes(String(option))}
                onChange={() => toggle(option)}
              />
            }
            label={formatValue(option)}
            sx={{
              margin: 0,
              px: '4px',
              '& .MuiFormControlLabel-label': { fontSize: '13px', color: 'text.main' },
            }}
          />
        ))}
      </Stack>

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
        <Button variant="secondary" size="small" onClick={reset}>
          {t('commonActionsReset')}
        </Button>
        <Button variant="primary" size="small" onClick={apply}>
          {t('commonActionsApply')}
        </Button>
      </Stack>
    </Box>
  )
}

export default SetFilterPopover
